import * as React from 'react';
import { Tournament } from 'Src/models/tournament';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Button } from 'Src/components/Button/Button';
import { DateTimePickerField } from 'Src/components/DateTimePickerField/DateTimePickerField';
import {
  getSchoolName,
  getName,
  getNameByPlaceOrPostcode,
  getSchoolNameAndTeamName,
  searchFunctionPlacesAndPostcodes,
  searchFunctionSports,
  searchFunctionTournamentParticipants,
  searchFunctionTournamentTeams
} from 'Src/helpers/autocomplete/autocomplete';
import { GENDER, SPORT_GENDER_SERVER_TO_CLIENT_MAPPING } from 'Src/consts/common';
import { Autocomplete } from 'Src/components/Autocomplete/Autocomplete';
import { AppUser } from 'Src/views/App/App';
import { isIndividualTournament, isMiniTeamTournament, isTeamTournament } from 'Src/helpers/tournament/tournament';
import { Multiselect } from 'Src/components/Multiselect/Multiselect';
import { getSelectOptionForTournamentEventAgesGroup } from 'Src/helpers/table/select';
import { formatTournamentAges } from 'Src/helpers/ageGroups';
import { DatePicker as SitDatePicker } from 'Src/components/DatePicker/DatePicker';
import { TimePicker } from 'Src/components/TimePicker/TimePicker';
import propz from 'propz';
import { SCHOOL_GENDER } from 'Src/consts/school';
import './TournamentAddEventForm.scss';

interface Props {
  tournament: Tournament;
  onSubmit: (data: any) => void | Promise<void>;
  onCancel: () => void;
  user: AppUser;
  participants: any[];
  onDirtyChange?: (isDirty: boolean) => void;
  isSubmitting?: boolean;
}

interface State {
  teams: any[];
  participants: any[];
  ages: number[];
  gender: string;
  individualTab: 'details' | 'teams';
  schoolSearch: string;
  schoolAppliedSearch: string;
}

export class TournamentAddEventForm extends React.Component<Props, State> {
  constructor(props) {
    super(props);

    const { tournament } = props;
    const { ages } = tournament;

    const gender = this.getGender();

    this.state = {
      teams: [],
      participants: props.participants,
      ages: isIndividualTournament(tournament) ? [] : ages,
      gender: gender,
      individualTab: 'details',
      schoolSearch: '',
      schoolAppliedSearch: ''
    };
  }

  getDefaultEventDurationMinutes = () => {
    const configuredDuration = Number(propz.get(this.props.tournament, ['eventDefaults', 'durationMinutes'], 60));
    return Number.isFinite(configuredDuration) && configuredDuration > 0 ? configuredDuration : 60;
  };

  getEventSchema() {
    const { tournament } = this.props;
    const dateRangeFields = {
      start: Yup.date()
        .required('Required')
        .test('start-before-end', 'The start date/time must be earlier than the finish date/time.', function(start) {
          const end = this.parent && this.parent.end;
          return !start || !end || start <= end;
        }),
      end: Yup.date()
        .required('Required')
        .test('end-after-start', 'The finish date/time must be later than the start date/time.', function(end) {
          const start = this.parent && this.parent.start;
          return !start || !end || end >= start;
        })
    };

    let schema;

    switch (true) {
      case isTeamTournament(tournament):
      case isMiniTeamTournament(tournament):
        schema = Yup.object().shape({
          ...dateRangeFields,
          sportId: Yup.string().required('Required'),
          gender: Yup.string().required('Required'),
          groupId: Yup.string().required('Required'),
          teams: Yup.array()
            .min(2)
            .max(2)
        });
        break;
      case isIndividualTournament(tournament):
        schema = Yup.object().shape({
          ...dateRangeFields,
          sportId: Yup.string().required('Required'),
          gender: Yup.string().required('Required'),
          ages: Yup.array().min(1, 'Select at least one age group'),
          participants: Yup.array().min(1, 'Include at least one school')
        });
        break;
      default:
        schema = Yup.object().shape({
          ...dateRangeFields,
          sportId: Yup.string().required('Required'),
          gender: Yup.string().required('Required')
        });
        break;
    }

    return schema;
  }

  renderGenderOptions(sport) {
    const { tournament } = this.props;
    const { isMixedTeamsAvailable } = tournament;

    if (typeof sport === 'undefined') {
      return <option value={''}>Select game</option>;
    }

    let genderOptions = [];

    if (sport.genders.maleOnly) {
      genderOptions.push(
        <option key="gender_option_male" value={GENDER.MALE_ONLY}>
          {SPORT_GENDER_SERVER_TO_CLIENT_MAPPING.MALE_ONLY}
        </option>
      );
    }

    if (sport.genders.femaleOnly) {
      genderOptions.push(
        <option key="gender_option_female" value={GENDER.FEMALE_ONLY}>
          {SPORT_GENDER_SERVER_TO_CLIENT_MAPPING.FEMALE_ONLY}
        </option>
      );
    }

    if (sport.genders.mixed && isMixedTeamsAvailable) {
      genderOptions.push(
        <option key="gender_option_mixed" value={GENDER.MIXED}>
          {SPORT_GENDER_SERVER_TO_CLIENT_MAPPING.MIXED}
        </option>
      );
    }

    return genderOptions;
  }

  renderCustomGroupsOptions() {
    const { tournament } = this.props;
    const groupStageIds = new Set(
      (tournament.tournamentLayout || [])
        .filter(stage => stage.type === 'GROUP')
        .map(stage => String(stage.id || (stage as any)._id))
    );
    const customGroups = (tournament.customGroups || []).filter(group =>
      groupStageIds.has(String(group.stageId))
    );

    if (customGroups.length === 0) {
      return <option value={''}>Not active option</option>;
    }

    return customGroups.map(customGroup => {
      return (
        <option key={customGroup.id} value={customGroup.id}>
          {customGroup.name}
        </option>
      );
    });
  }

  getSports = (text: string) => {
    const user = this.props.user;
    return searchFunctionSports(user, text);
  };

  getVenues = (text: string) => {
    const user = this.props.user;
    return searchFunctionPlacesAndPostcodes(user, text);
  };

  getGender() {
    const { tournament } = this.props;
    const { gender, isMixedTeamsAvailable } = tournament;
    const isGenderMixed = gender === GENDER.MIXED;

    switch (true) {
      case isGenderMixed && !isMixedTeamsAvailable:
        return GENDER.MALE_ONLY;
      default:
        return gender;
    }
  }

  getEventInitial() {
    const { tournament, participants } = this.props;
    const { startTime, sportId, sport, ages, venue } = tournament;

    const gender = this.getGender();

    const startTimeDateObj = new Date(startTime);
    const endTimeDateObj = new Date(startTimeDateObj);
    endTimeDateObj.setMinutes(startTimeDateObj.getMinutes() + this.getDefaultEventDurationMinutes());

    const groupStageIds = new Set(
      (tournament.tournamentLayout || [])
        .filter(stage => stage.type === 'GROUP')
        .map(stage => String(stage.id || (stage as any)._id))
    );
    const customGroups = (tournament.customGroups || []).filter(group =>
      groupStageIds.has(String(group.stageId))
    );

    let eventInitial;

    switch (true) {
      case isTeamTournament(tournament):
      case isMiniTeamTournament(tournament):
        eventInitial = {
          start: startTimeDateObj,
          end: endTimeDateObj,
          sport: sport,
          sportId: sportId,
          gender: gender,
          ages: ages,
          groupId: customGroups.length === 0 ? '' : customGroups[0].id,
          venue: venue,
          teams: []
        };
        break;
      case isIndividualTournament(tournament):
        eventInitial = {
          start: startTimeDateObj,
          end: endTimeDateObj,
          sport: sport,
          sportId: sportId,
          gender: gender,
          ages: [],
          venue: venue,
          participants: participants,
          shortDescription: '',
          individualEventFormat: { category: 'OTHER', classification: 'OPEN', stage: 'STRAIGHT_FINAL', publicNote: '' }
        };
        break;
      default:
        eventInitial = {
          start: startTimeDateObj,
          end: endTimeDateObj,
          sport: sport,
          sportId: sportId,
          gender: gender,
          ages: ages,
          venue: venue
        };
    }

    return eventInitial;
  }

  getParticipantCompatibilityWarnings = (participant: any, eventAges: any[], eventGender: string) => {
    const school = participant.school || participant;
    const availableAges = Array.isArray(school.availableAges)
      ? school.availableAges.map(Number)
      : Array.isArray(participant.availableAges) ? participant.availableAges.map(Number) : [];
    const availableGender = school.availableGender || participant.availableGender || '';
    const warnings: string[] = [];
    if (availableAges.length === 0) {
      warnings.push('Eligible ages are not specified; age compatibility cannot be verified.');
    } else if (!(eventAges || []).map(Number).some(age => availableAges.includes(age))) {
      warnings.push(`School ages (${formatTournamentAges(availableAges)}) do not match this event.`);
    }
    if (eventGender !== GENDER.MIXED) {
      if (!availableGender) {
        warnings.push('Eligible gender is not specified; gender compatibility cannot be verified.');
      } else {
        const expected = eventGender === GENDER.MALE_ONLY ? SCHOOL_GENDER.MALE : SCHOOL_GENDER.FEMALE;
        if (availableGender !== expected && availableGender !== SCHOOL_GENDER.MIXED) {
          warnings.push(`School gender (${String(availableGender).toLowerCase()}) does not match this event.`);
        }
      }
    }
    return warnings;
  };

  renderTeams(teams, setFieldValue) {
    return teams.map(team => {
      return (
        <div key={team.id} className={'mb-3'}>
          <Autocomplete
            onSelect={() => {}}
            searchFunction={() => {}}
            getElementTitle={getSchoolNameAndTeamName}
            defaultItem={team}
            disabled
          />
          <div className="eClearItemButton ml-3">
            <button
              type="button"
              className="close"
              onClick={() => {
                const teamsFiltered = teams.filter(t => t.id !== team.id);
                setFieldValue('teams', teamsFiltered);
                this.setState({
                  teams: teamsFiltered
                });
              }}
            >
              <span>&times;</span>
            </button>
          </div>
        </div>
      );
    });
  }

  renderParticipants(participants, setFieldValue) {
    return participants.map(participant => {
      return (
        <div key={participant.schoolId} className={'mb-3'}>
          <Autocomplete
            onSelect={() => {}}
            searchFunction={() => {}}
            getElementTitle={getSchoolName}
            defaultItem={participant}
            disabled
          />
          <div className="eClearItemButton ml-3">
            <button
              type="button"
              className="close"
              onClick={() => {
                const participantsFiltered = participants.filter(p => p.id !== participant.id);
                setFieldValue('participants', participantsFiltered);
                this.setState({
                  participants: participantsFiltered
                });
              }}
            >
              <span>&times;</span>
            </button>
          </div>
        </div>
      );
    });
  }

  applyDatePart = (value: Date, date: Date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate(), value.getHours(), value.getMinutes(), 0, 0);

  applyTimePart = (value: Date, time: Date) =>
    new Date(value.getFullYear(), value.getMonth(), value.getDate(), time.getHours(), time.getMinutes(), 0, 0);

  getVenueLabel = () => {
    const venue: any = this.props.tournament.venue || {};
    const name = propz.get(
      venue,
      ['placeData', 'name'],
      propz.get(venue, ['name'], propz.get(venue, ['placeName'], 'Venue'))
    );
    const postcode = propz.get(venue, ['postcodeData', 'postcode'], propz.get(venue, ['postcode'], ''));
    return postcode ? `${name} · ${postcode}` : name;
  };

  renderTeamFixtureForm = () => {
    const eventInitial = { ...this.getEventInitial(), shortDescription: '', pitchId: '' };
    const { tournament } = this.props;
    const genderLabel = SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[eventInitial.gender] || eventInitial.gender;
    const ageLabel = formatTournamentAges(eventInitial.ages || []);

    return (
      <div className="tournamentFixtureForm">
        <div className="tournamentFixtureForm__chips" aria-label="Fixture details">
          <span className="is-sport">{tournament.sport?.name || 'Sport'}</span>
          <span className="is-age">{ageLabel || 'All ages'}</span>
          <span className="is-gender">{genderLabel}</span>
          <span className="is-venue">{this.getVenueLabel()}</span>
        </div>
        <Formik
          initialValues={eventInitial}
          validationSchema={this.getEventSchema()}
          onSubmit={values => this.props.onSubmit(values)}
          validateOnMount
        >
          {({ touched, errors, setFieldValue, values, dirty, isValid }) => (
            <Form>
              <FixtureDirtyObserver dirty={dirty} onDirtyChange={this.props.onDirtyChange} />
              <div className="tournamentFixtureForm__timing">
                <Field name="start">
                  {() => (
                    <SitDatePicker
                      label="Date"
                      value={values.start}
                      onChange={(date: Date | null) => {
                        if (!date) return;
                        const duration = Math.max(0, values.end.getTime() - values.start.getTime());
                        const start = this.applyDatePart(values.start, date);
                        setFieldValue('start', start);
                        setFieldValue('end', new Date(start.getTime() + duration));
                      }}
                      format="DD MMM YYYY"
                      required
                      clearable={false}
                      className="tournamentFixtureForm__picker"
                      error={touched.start && errors.start ? String(errors.start) : undefined}
                    />
                  )}
                </Field>
                <Field name="start">
                  {() => (
                    <TimePicker
                      label="Start time"
                      value={values.start}
                      onChange={(time: Date | null) => {
                        if (!time) return;
                        const start = this.applyTimePart(values.start, time);
                        setFieldValue('start', start);
                        const durationMinutes = isIndividualTournament(tournament)
                          ? this.getDefaultEventDurationMinutes()
                          : 60;
                        setFieldValue('end', new Date(start.getTime() + durationMinutes * 60 * 1000));
                      }}
                      minuteStep={5}
                      required
                      showConfirm
                      clearable={false}
                      className="tournamentFixtureForm__picker"
                    />
                  )}
                </Field>
                <Field name="end">
                  {() => (
                    <TimePicker
                      label="Finish time"
                      value={values.end}
                      onChange={(time: Date | null) => {
                        if (!time) return;
                        let end = this.applyTimePart(values.start, time);
                        if (end < values.start) end = new Date(end.getTime() + 24 * 60 * 60 * 1000);
                        setFieldValue('end', end);
                      }}
                      minuteStep={5}
                      required
                      showConfirm
                      clearable={false}
                      className="tournamentFixtureForm__picker"
                      error={touched.end && errors.end ? String(errors.end) : undefined}
                    />
                  )}
                </Field>
              </div>

              <div className="tournamentFixtureForm__row">
                <label>
                  <span>
                    Group <i className="tournamentFixtureForm__required">*</i>
                  </span>
                  <Field name="groupId" component="select">
                    {this.renderCustomGroupsOptions()}
                  </Field>
                  {touched.groupId && errors.groupId ? (
                    <small className="is-error">{String(errors.groupId)}</small>
                  ) : null}
                </label>
                <label>
                  <span>Pitch</span>
                  <Field name="pitchId" component="select">
                    <option value="">Not assigned</option>
                    {(tournament.pitches || []).map(pitch => (
                      <option key={pitch.id} value={pitch.id}>
                        {pitch.name}
                      </option>
                    ))}
                  </Field>
                </label>
              </div>

              <label className="tournamentFixtureForm__field">
                <span>Short description</span>
                <Field
                  name="shortDescription"
                  type="text"
                  placeholder="Optional name shown in fixture lists and on the public site"
                  onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                    if (event.key === 'Enter') event.preventDefault();
                  }}
                />
              </label>

              <div className="tournamentFixtureForm__teams">
                <div>
                  <strong>
                    Teams <i className="tournamentFixtureForm__required">*</i>
                  </strong>
                </div>
                {this.renderTeams(values.teams, setFieldValue)}
                {values.teams.length < 2 ? (
                  <Autocomplete
                    searchFunction={this.getTeams}
                    getElementTitle={getSchoolNameAndTeamName}
                    customClass="mFullWidth mb-0"
                    defaultItem={undefined}
                    onSelect={team => {
                      const nextTeams = [].concat(values.teams, team);
                      setFieldValue('teams', nextTeams);
                      this.setState({ teams: nextTeams });
                    }}
                  />
                ) : null}
                {touched.teams && errors.teams ? <small className="is-error">Select exactly two teams.</small> : null}
              </div>

              <footer className="tournamentFixtureForm__actions">
                <button type="button" className="sit-btn sit-btn--secondary" onClick={this.props.onCancel}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="sit-btn sit-btn--primary"
                  disabled={!isValid || values.teams.length !== 2 || this.props.isSubmitting}
                >
                  {this.props.isSubmitting ? 'Saving…' : 'Save fixture'}
                </button>
              </footer>
            </Form>
          )}
        </Formik>
      </div>
    );
  };

  getTeams = (text: string) => {
    const { teams, ages, gender } = this.state;
    const { user, tournament } = this.props;

    return searchFunctionTournamentTeams(user, text, tournament, teams, ages, gender);
  };

  getSchools = (text: string) => {
    const { participants } = this.state;
    const { user, tournament } = this.props;

    return searchFunctionTournamentParticipants(user, text, tournament, participants);
  };

  render() {
    const eventInitial = this.getEventInitial();
    const { tournament, user } = this.props;
    const { gender } = tournament;
    const { teams } = this.state;

    const isTeamOrMiniTeam = isTeamTournament(tournament) || isMiniTeamTournament(tournament);
    const isGenderMixed = gender === GENDER.MIXED;

    if (isTeamOrMiniTeam) {
      return this.renderTeamFixtureForm();
    }

    return (
      <div className="tournamentIndividualEventForm">
        <Formik
          initialValues={eventInitial}
          validationSchema={this.getEventSchema()}
          onSubmit={(values, actions) => {
            if (this.state.individualTab !== 'teams') {
              actions.setSubmitting(false);
              this.setState({ individualTab: 'teams' });
              return;
            }
            return this.props.onSubmit(values);
          }}
          validateOnMount
        >
          {({ touched, errors, setFieldValue, values, dirty, isValid, validateForm, setTouched }) => {
            const ageOptions: any[] = getSelectOptionForTournamentEventAgesGroup(tournament, user) as any[];
            const genderOptions = [
              values.sport?.genders?.maleOnly ? GENDER.MALE_ONLY : null,
              values.sport?.genders?.femaleOnly ? GENDER.FEMALE_ONLY : null,
              values.sport?.genders?.mixed && tournament.isMixedTeamsAvailable ? GENDER.MIXED : null
            ].filter(Boolean) as string[];
            const participantId = (participant: any) => String(participant.id || participant.schoolId);
            const selectedIds = new Set((values.participants || []).map(participantId));
            const filteredSchools = (this.props.participants || []).filter((participant: any) =>
              String(getSchoolName(participant) || '')
                .toLowerCase()
                .includes(this.state.schoolAppliedSearch.trim().toLowerCase())
            );
            const goToTeams = async (event?: React.SyntheticEvent) => {
              event?.preventDefault();
              event?.stopPropagation();
              const formErrors: any = await validateForm();
              if (formErrors.start || formErrors.end || formErrors.sportId || formErrors.gender || formErrors.ages) {
                setTouched({ start: true, end: true, sportId: true, gender: true, ages: true });
                return;
              }
              this.setState({ individualTab: 'teams' });
            };

            return (
              <Form>
                <FixtureDirtyObserver dirty={dirty} onDirtyChange={this.props.onDirtyChange} />
                <nav className="tournamentIndividualEventForm__tabs" aria-label="Event form sections">
                  <button
                    type="button"
                    className={this.state.individualTab === 'details' ? 'is-active' : ''}
                    onClick={() => this.setState({ individualTab: 'details' })}
                  >
                    Details
                  </button>
                  <button
                    type="button"
                    className={this.state.individualTab === 'teams' ? 'is-active' : ''}
                    onClick={event => void goToTeams(event)}
                  >
                    Teams <b>{values.participants.length}</b>
                  </button>
                </nav>

                {this.state.individualTab === 'details' ? (
                  <section className="tournamentIndividualEventForm__details">
                    <div className="tournamentIndividualEventForm__timing">
                      <SitDatePicker
                        mode="datetime"
                        label="Starts"
                        value={values.start}
                        onChange={(date: Date | null) => {
                          if (!date) return;
                          const duration = Math.max(0, values.end.getTime() - values.start.getTime());
                          setFieldValue('start', date);
                          setFieldValue('end', new Date(date.getTime() + duration));
                        }}
                        minuteStep={5}
                        format="DD MMM YYYY, HH:mm"
                        centered
                        clearable={false}
                        required
                        size="sm"
                        error={touched.start && errors.start ? String(errors.start) : undefined}
                      />
                      <SitDatePicker
                        mode="datetime"
                        label="Finishes"
                        value={values.end}
                        min={values.start}
                        onChange={(date: Date | null) => date && setFieldValue('end', date)}
                        minuteStep={5}
                        format="DD MMM YYYY, HH:mm"
                        centered
                        clearable={false}
                        required
                        size="sm"
                        error={touched.end && errors.end ? String(errors.end) : undefined}
                      />
                    </div>

                    <label className="tournamentIndividualEventForm__field">
                      <span>Event/Race <i>*</i></span>
                      <Autocomplete
                        searchFunction={this.getSports}
                        getElementTitle={getName}
                        customClass="mFullWidth"
                        defaultItem={values.sport}
                        onSelect={(sport: any) => {
                          setFieldValue('sportId', sport.id);
                          setFieldValue('sport', sport);
                          const nextGender = sport.genders?.maleOnly
                            ? GENDER.MALE_ONLY
                            : sport.genders?.femaleOnly
                            ? GENDER.FEMALE_ONLY
                            : sport.genders?.mixed && tournament.isMixedTeamsAvailable
                            ? GENDER.MIXED
                            : GENDER.MALE_ONLY;
                          setFieldValue('gender', nextGender);
                          this.setState({ gender: nextGender });
                        }}
                      />
                      {touched.sportId && errors.sportId ? <small>{String(errors.sportId)}</small> : null}
                    </label>

                    <label className="tournamentIndividualEventForm__field">
                      <span>Short description</span>
                      <Field
                        name="shortDescription"
                        type="text"
                        maxLength={160}
                        placeholder="Add a short name or important detail shown in event lists and on the public site"
                        onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                          if (event.key === 'Enter') event.preventDefault();
                        }}
                      />
                      <small className="is-hint">Optional · up to 160 characters</small>
                    </label>

                    <div className="tournamentIndividualEventForm__audience">
                      <label className="tournamentIndividualEventForm__field">
                        <span>Event category</span>
                        <Field as="select" name="individualEventFormat.category">
                          <option value="TRACK">Track</option><option value="FIELD">Field</option>
                          <option value="POOL">Pool</option><option value="COURSE">Course</option>
                          <option value="COURT">Court</option><option value="OTHER">Other</option>
                        </Field>
                      </label>
                      <label className="tournamentIndividualEventForm__field">
                        <span>Competition round</span>
                        <Field as="select" name="individualEventFormat.stage">
                          <option value="STRAIGHT_FINAL">Straight final</option><option value="HEAT">Heat</option>
                          <option value="QUALIFIER">Qualifier</option><option value="SEMI_FINAL">Semi-final</option>
                          <option value="FINAL">Final</option>
                        </Field>
                      </label>
                      <label className="tournamentIndividualEventForm__field">
                        <span>Classification</span>
                        <Field as="select" name="individualEventFormat.classification"><option value="OPEN">Open</option><option value="PARA">Para</option></Field>
                      </label>
                    </div>
                    {values.individualEventFormat?.stage === 'HEAT' && <div className="tournamentIndividualEventForm__timing">
                      <label className="tournamentIndividualEventForm__field"><span>Heat number</span><Field type="number" min="1" name="individualEventFormat.heatNumber" /></label>
                      <label className="tournamentIndividualEventForm__field"><span>Total heats</span><Field type="number" min="1" name="individualEventFormat.heatCount" /></label>
                    </div>}
                    <label className="tournamentIndividualEventForm__field">
                      <span>Public progression note</span>
                      <Field name="individualEventFormat.publicNote" maxLength={500} placeholder="For example: six heats, fastest six qualify for the final" />
                      <small className="is-hint">Optional. Shown in the timetable and live event view.</small>
                    </label>

                    <div className="tournamentIndividualEventForm__audience">
                      <div>
                        <strong>Gender <i>*</i></strong>
                        <div className="tournamentIndividualEventForm__chips">
                          {genderOptions.map(option => (
                            <button
                              type="button"
                              key={option}
                              className={values.gender === option ? 'is-selected is-gender' : ''}
                              onClick={() => {
                                setFieldValue('gender', option);
                                this.setState({ gender: option });
                              }}
                            >
                              {SPORT_GENDER_SERVER_TO_CLIENT_MAPPING[option] || option}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <strong>Ages <i>*</i></strong>
                        <div className="tournamentIndividualEventForm__chips">
                          {ageOptions.map(option => {
                            const selected = (values.ages || []).map(Number).includes(Number(option.value));
                            return (
                              <button
                                type="button"
                                key={option.value}
                                className={selected ? 'is-selected is-age' : ''}
                                onClick={() => {
                                  const nextAges = selected
                                    ? values.ages.filter((age: any) => Number(age) !== Number(option.value))
                                    : [...values.ages, option.value];
                                  setFieldValue('ages', nextAges);
                                  this.setState({ ages: nextAges.map(Number) });
                                }}
                              >
                                {option.text || option.label}
                              </button>
                            );
                          })}
                        </div>
                        {touched.ages && errors.ages ? <small className="is-error">{String(errors.ages)}</small> : null}
                      </div>
                    </div>
                    <div className="tournamentIndividualEventForm__venue">
                      <span>Venue</span>
                      <strong>{this.getVenueLabel()}</strong>
                    </div>
                  </section>
                ) : (
                  <section className="tournamentIndividualEventForm__schools">
                    <header>
                      <div>
                        <strong>Participating schools</strong>
                        <span>{values.participants.length} included</span>
                      </div>
                      <input
                        type="search"
                        value={this.state.schoolSearch}
                        placeholder="Search by school name"
                        onChange={event => this.setState({ schoolSearch: event.target.value })}
                        onKeyDown={event => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            this.setState({ schoolAppliedSearch: this.state.schoolSearch });
                          }
                        }}
                      />
                    </header>
                    <div className="tournamentIndividualEventForm__schoolList">
                      {filteredSchools.map((participant: any) => {
                        const included = selectedIds.has(participantId(participant));
                        const schoolName = String(getSchoolName(participant) || 'School');
                        const logo = participant.school?.pic || participant.pic || '';
                        const compatibilityWarnings = this.getParticipantCompatibilityWarnings(
                          participant,
                          values.ages || [],
                          values.gender
                        );
                        return (
                          <label key={participantId(participant)} className={`${!included ? 'is-excluded' : ''} ${compatibilityWarnings.length ? 'has-compatibilityWarning' : ''}`}>
                            <input
                              type="checkbox"
                              checked={included}
                              onChange={() => {
                                const next = included
                                  ? values.participants.filter((item: any) => participantId(item) !== participantId(participant))
                                  : [...values.participants, participant];
                                setFieldValue('participants', next);
                                this.setState({ participants: next });
                              }}
                            />
                            {logo ? <img src={logo} alt="" /> : <span className="is-logo">{schoolName.charAt(0)}</span>}
                            <span className="tournamentIndividualEventForm__schoolName">
                              <strong>{schoolName}</strong>
                              {compatibilityWarnings.map(warning => (
                                <small key={warning} className="is-compatibilityWarning">⚠ {warning}</small>
                              ))}
                            </span>
                            <small>{included ? 'Included' : 'Excluded'}</small>
                          </label>
                        );
                      })}
                      {!filteredSchools.length ? <p>No schools match this search.</p> : null}
                    </div>
                    {touched.participants && errors.participants ? (
                      <small className="is-error">{String(errors.participants)}</small>
                    ) : null}
                  </section>
                )}

                <footer className="tournamentIndividualEventForm__actions">
                  <button type="button" className="sit-btn sit-btn--secondary" onClick={this.props.onCancel}>
                    Cancel
                  </button>
                  {this.state.individualTab === 'details' ? (
                    <button type="button" className="sit-btn sit-btn--primary" onClick={event => void goToTeams(event)}>
                      Continue <span aria-hidden="true">→</span>
                    </button>
                  ) : (
                    <button type="submit" className="sit-btn sit-btn--primary" disabled={!isValid || this.props.isSubmitting}>
                      {this.props.isSubmitting ? 'Creating…' : 'Create event'}
                    </button>
                  )}
                </footer>
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  }
}

const FixtureDirtyObserver = ({
  dirty,
  onDirtyChange
}: {
  dirty: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
}) => {
  React.useEffect(() => {
    if (onDirtyChange) onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  return null;
};
